import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import BackButton from '../components/BackButton';
import { useDemo, useAppMode } from '../context/DemoContext';
import {
  Flame, Trophy, Target, Users, Clock, Award,
  TrendingUp, Zap, Shield, Crown, Star, Eye, Skull, Info, Swords, AlertTriangle, Rocket, X, ExternalLink,
} from 'lucide-react';
import { readSeason, executeBurn, getTokenBalance, DEVNET, DECIMALS_FACTOR, DECIMALS_FACTOR_BIGINT, fromRaw, explorerTx } from '../lib/solana/devnet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  newcomer: '#666', bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700',
  platinum: '#e5e4e2', diamond: '#b9f2ff', obsidian: '#1a1a2e', mythic: '#ff00ff'
};

const PHASE_CONFIG = {
  ignition: { color: '#ff4500', label: 'IGNITION', desc: 'Early burn bonus active', tag: 'LAUNCH PHASE' },
  calibration: { color: '#3b82f6', label: 'CALIBRATION', desc: 'Target adjusting', tag: 'ADAPTING' },
  grind: { color: '#9ca3af', label: 'THE GRIND', desc: 'Standard rates', tag: 'STEADY STATE' },
  fever: { color: '#fbbf24', label: 'FEVER', desc: 'Bonus if target >80%', tag: 'HOT STREAK' },
  wire: { color: '#ef4444', label: 'THE WIRE', desc: 'Final push. 1.5x burns.', tag: 'CRITICAL' },
};

const ESCALATION_THEMES = {
  calm: { color: '#22c55e', bg: 'rgba(34,197,94,0.06)' },
  tension: { color: '#fbbf24', bg: 'rgba(251,191,36,0.06)' },
  crisis: { color: '#ef4444', bg: 'rgba(239,68,68,0.06)' },
  meltdown: { color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
};

const tierMultiplierLabel = (m) => m === 1 ? '1x' : `${m}x`;

/* ═══ TIME REMAINING ═══ */
function TimeRemaining({ endDate }) {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const tick = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setRemaining('ENDED'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setRemaining(`${d}d ${h}h ${m}m`);
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [endDate]);
  return <span>{remaining}</span>;
}

/* ═══ BURN PORTAL ═══ Cinematic war-room style, shared with demo */
function BurnPortal({ walletAddress, onBurn, isDemo = false, isLive = false }) {
  const [selected, setSelected] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [burning, setBurning] = useState(false);
  const [burnStep, setBurnStep] = useState('');
  const [result, setResult] = useState(null);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const { publicKey, signTransaction } = useWallet();

  // ─── Live-mode on-chain state — polls Season PDA + token balance every 12s
  const [chain, setChain] = useState({ season: null, gfg: null, lastUpdated: null });

  const refreshChain = useCallback(async () => {
    if (!isLive) return;
    try {
      // Season is a public on-chain read — fetch even without a connected wallet.
      const seasonP = readSeason();
      const gfgP = walletAddress ? getTokenBalance(walletAddress) : Promise.resolve(null);
      const [season, gfg] = await Promise.all([seasonP, gfgP]);
      setChain({ season, gfg, lastUpdated: Date.now() });
    } catch {
      // network blip — keep previous values
    }
  }, [isLive, walletAddress]);

  useEffect(() => {
    if (!isLive) { setChain({ season: null, gfg: null, lastUpdated: null }); return; }
    refreshChain();
    const id = setInterval(refreshChain, 12000);
    return () => clearInterval(id);
  }, [isLive, refreshChain]);

  const tiers = [
    { label: '10,000',  amount: 10000,  sp: 345,  bonus: null,     tier: 'Ember' },
    { label: '50,000',  amount: 50000,  sp: 580,  bonus: '+15%',   tier: 'Pyre' },
    { label: '100,000', amount: 100000, sp: 830,  bonus: '+25%',   tier: 'Inferno' },
  ];

  // Effective burn amount
  const parsedCustom = parseInt(customAmount);
  // Live mode (devnet) accepts any positive amount — the on-chain test
  // season target is small. Demo/normal still enforces the 1000 minimum.
  const minBurn = isLive ? 1 : 1000;
  const hasCustom = Number.isFinite(parsedCustom) && parsedCustom >= minBurn;
  const burnAmount = hasCustom ? parsedCustom : selected;

  // Live SP preview: logarithmic curve matches backend calculate_burn_points
  // base_rate=100, scale=1000 → sp = 100 * log2(1 + amount/1000)
  const previewSP = hasCustom
    ? Math.max(0, Math.floor(100 * Math.log2(1 + parsedCustom / 1000)))
    : 0;
  // Tier name based on custom amount
  const previewTier = hasCustom
    ? (parsedCustom >= 100000 ? { name: 'Inferno', color: '#ef4444', bonus: '+25%' }
       : parsedCustom >= 50000 ? { name: 'Pyre', color: '#ff8c00', bonus: '+15%' }
       : { name: 'Ember', color: '#fbbf24', bonus: null })
    : null;

  const handleBurn = async () => {
    if (!burnAmount || burnAmount < minBurn) return;

    // ─── DEMO MODE: call the demo endpoint (real backend, no chain) ───
    if (isDemo) {
      if (!walletAddress) return;
      setBurning(true); setResult(null); setBurnStep('verifying');
      try {
        const res = await fetch(`${API_URL}/api/seasons/burn/demo`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet_address: walletAddress, amount: burnAmount }),
        });
        const data = await res.json();
        if (data.success) {
          setResult(data);
          setSelected(null); setCustomAmount('');
          if (onBurn) onBurn();
        } else if (res.status === 400 && /no active season/i.test(data.detail || '')) {
          // No active season — show launch modal so admin knows to start a demo season
          setShowLaunchModal(true);
        } else {
          setResult({ error: data.detail || 'Demo burn failed' });
        }
      } catch (err) {
        setResult({ error: err?.message || 'Demo burn failed' });
      }
      setBurning(false); setBurnStep('');
      return;
    }

    // ─── LIVE MODE: real Solana devnet burn via burn_tokens instruction ───
    if (isLive) {
      if (!walletAddress || !publicKey || !signTransaction) {
        setResult({ error: 'Connect Phantom to burn on devnet' });
        return;
      }
      // Sanity check token balance before signing.
      const bal = chain.gfg ? Number(chain.gfg.uiAmount || 0) : 0;
      if (bal < burnAmount) {
        setResult({
          error: `Need ${burnAmount.toLocaleString()} $GFG · wallet has ${bal.toLocaleString()}. Mint test tokens or lower the amount.`,
        });
        return;
      }
      // Pre-flight: the on-chain program enforces `BurnAmountTooLarge` (Anchor
      // error 6010) when a single burn exceeds the season's remaining capacity.
      // Cap the UI amount before we even ask Phantom to sign.
      const milestoneRaw = chain.season ? BigInt(chain.season.burn_milestone) : 0n;
      const burnedRaw = chain.season ? BigInt(chain.season.total_burned) : 0n;
      const remainingRaw = milestoneRaw > burnedRaw ? milestoneRaw - burnedRaw : 0n;
      const remainingUI = fromRaw(remainingRaw);
      if (chain.season && remainingUI <= 0) {
        setResult({ error: `Season ${chain.season.season_number} milestone already reached — wait for next season or admin re-init.` });
        return;
      }
      if (chain.season && burnAmount > remainingUI) {
        setResult({
          error: `Devnet season target has only ${remainingUI.toLocaleString(undefined, { maximumFractionDigits: 6 })} $GFG capacity remaining — try a smaller amount (Anchor error 6010: BurnAmountTooLarge).`,
        });
        return;
      }
      setBurning(true); setResult(null);
      try {
        setBurnStep('signing');
        const seasonNumber = chain.season?.season_number || 1;
        // Convert UI amount → raw base units using the mint's decimals
        // (single source of truth: DEVNET.decimals in /lib/solana/devnet.js).
        // Without this multiplier, on-chain treats "1000" as 0.001 $GFG.
        const rawAmount = BigInt(Math.floor(burnAmount)) * DECIMALS_FACTOR_BIGINT;
        const sig = await executeBurn({
          walletAdapter: { publicKey, signTransaction },
          amount: rawAmount,
          seasonNumber,
        });
        setBurnStep('confirming');
        // Optimistic UI — refetch the on-chain Season immediately for new milestone.
        await refreshChain();
        setResult({
          success: true,
          live: true,
          signature: sig,
          explorer: explorerTx(sig),
          amount_burned: burnAmount,
          season_number: seasonNumber,
        });
        setSelected(null); setCustomAmount('');
        if (onBurn) onBurn();
      } catch (err) {
        const msg = err?.message || String(err);
        const friendly = /User rejected|cancelled|denied/i.test(msg)
          ? 'Transaction cancelled in wallet'
          : msg;
        setResult({ error: friendly });
      }
      setBurning(false); setBurnStep('');
      return;
    }
    // ─── REAL MODE: requires wallet signing ───
    if (!walletAddress || !publicKey) return;
    setBurning(true); setResult(null);
    try {
      setBurnStep('signing');
      const prepRes = await fetch(`${API_URL}/api/seasons/burn/prepare`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, amount: burnAmount }),
      });
      const prepData = await prepRes.json();
      if (!prepData.success) {
        const errMsg = prepData.detail || prepData.message || 'Failed';
        // Show modal for token-not-launched; inline for other errors
        if (/token not launched|burn portal will activate|placeholder.*mint/i.test(errMsg)) {
          setShowLaunchModal(true);
        } else {
          setResult({ error: errMsg });
        }
        setBurning(false); setBurnStep('');
        return;
      }
      const { Connection, PublicKey: PK, Transaction } = await import('@solana/web3.js');
      const { getAssociatedTokenAddress, createTransferInstruction, TOKEN_PROGRAM_ID } = await import('@solana/spl-token');
      const connection = new Connection(prepData.rpc_url, 'confirmed');
      const mintPubkey = new PK(prepData.token_mint);
      const burnPubkey = new PK(prepData.burn_address);
      const sourceATA = await getAssociatedTokenAddress(mintPubkey, publicKey);
      const destATA = await getAssociatedTokenAddress(mintPubkey, burnPubkey);
      const transaction = new Transaction();
      transaction.add(createTransferInstruction(sourceATA, destATA, publicKey, BigInt(prepData.amount_raw), [], TOKEN_PROGRAM_ID));
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash; transaction.feePayer = publicKey;
      const signedTx = await signTransaction(transaction);
      setBurnStep('confirming');
      const rawTx = signedTx.serialize();
      const signature = await connection.sendRawTransaction(rawTx, { skipPreflight: false, preflightCommitment: 'confirmed' });
      await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'confirmed');
      setBurnStep('verifying');
      const verifyRes = await fetch(`${API_URL}/api/seasons/burn/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, amount: burnAmount, signature, burn_id: prepData.burn_id }),
      });
      const verifyData = await verifyRes.json();
      if (verifyData.success) {
        setResult(verifyData);
        setSelected(null); setCustomAmount('');
        if (onBurn) onBurn();
      } else {
        setResult({ error: verifyData.detail || 'Verification failed' });
      }
    } catch (err) {
      const msg = err?.message || '';
      setResult({ error: msg.includes('rejected') || msg.includes('cancelled') ? 'Transaction cancelled' : msg || 'Burn failed' });
    }
    setBurning(false); setBurnStep('');
  };

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col gap-3 md:gap-5" data-testid="burn-portal-container">
      {/* ═══ LIVE MODE — on-chain Season status banner ═══ */}
      {isLive && (
        <div
          data-testid="burn-portal-live-banner"
          className="px-4 py-3"
          style={{
            background: 'linear-gradient(180deg, rgba(8,18,28,0.92) 0%, rgba(4,10,18,0.95) 100%)',
            border: '1px solid rgba(125,208,255,0.45)',
            borderLeft: '3px solid #7dd0ff',
            boxShadow: '0 0 18px rgba(125,208,255,0.12)',
          }}
        >
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span
                className="inline-block rounded-full"
                style={{
                  width: 8, height: 8,
                  background: '#7dd0ff',
                  boxShadow: '0 0 8px #7dd0ff, 0 0 14px rgba(125,208,255,0.6)',
                  animation: 'gfg-live-pulse 1.4s ease-in-out infinite',
                }}
              />
              <span
                className="text-[10px] tracking-[0.32em] font-bold uppercase"
                style={{ color: '#a8def7', fontFamily: "'Cinzel', serif" }}
              >
                DEVNET · LIVE
              </span>
              <a
                href={`https://explorer.solana.com/address/${DEVNET.seasonPda}?cluster=devnet`}
                target="_blank" rel="noopener noreferrer"
                className="text-[9.5px] tracking-[0.2em] hover:brightness-125 transition inline-flex items-center gap-1"
                style={{ color: '#7dd0ff', fontFamily: "'Courier New', monospace" }}
                data-testid="burn-portal-season-link"
              >
                view season<ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
            {chain.lastUpdated && (
              <span className="text-[9px] tracking-[0.18em]" style={{ color: 'rgba(125,208,255,0.55)', fontFamily: "'Courier New', monospace" }}>
                refreshed {Math.max(1, Math.floor((Date.now() - chain.lastUpdated) / 1000))}s ago
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]" style={{ fontFamily: "'Courier New', monospace" }}>
            <div>
              <p style={{ color: 'rgba(125,208,255,0.55)', letterSpacing: '0.18em' }}>SEASON</p>
              <p className="font-bold" style={{ color: '#a8def7' }}>
                {chain.season ? `S${chain.season.season_number}` : '—'}
              </p>
            </div>
            <div>
              <p style={{ color: 'rgba(125,208,255,0.55)', letterSpacing: '0.18em' }}>BURNED</p>
              <p className="font-bold" style={{ color: '#a8def7' }}>
                {chain.season ? fromRaw(chain.season.total_burned).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
              </p>
            </div>
            <div>
              <p style={{ color: 'rgba(125,208,255,0.55)', letterSpacing: '0.18em' }}>REMAINING</p>
              <p className="font-bold" style={{ color: '#a8def7' }}>
                {chain.season
                  ? (() => {
                      const m = BigInt(chain.season.burn_milestone);
                      const b = BigInt(chain.season.total_burned);
                      const r = m > b ? m - b : 0n;
                      const ui = fromRaw(r);
                      return ui.toLocaleString(undefined, { maximumFractionDigits: 6 });
                    })()
                  : '—'}
              </p>
            </div>
            <div>
              <p style={{ color: 'rgba(125,208,255,0.55)', letterSpacing: '0.18em' }}>YOUR $GFG</p>
              <p className="font-bold" style={{ color: '#a8def7' }}>
                {chain.gfg ? Number(chain.gfg.uiAmount).toLocaleString() : '—'}
              </p>
            </div>
          </div>
          {/* Live milestone bar */}
          {chain.season && Number(chain.season.burn_milestone) > 0 && (() => {
            const burned = Number(chain.season.total_burned);
            const target = Number(chain.season.burn_milestone);
            const pct = Math.min(100, (burned / target) * 100);
            return (
              <div
                className="mt-3 h-1.5 relative overflow-hidden"
                style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(125,208,255,0.25)' }}
                data-testid="burn-portal-live-progress"
              >
                <div
                  className="h-full transition-all duration-700"
                  style={{
                    width: `${pct}%`,
                    background: 'linear-gradient(90deg, #7dd0ff, #a892ff)',
                    boxShadow: '0 0 10px rgba(125,208,255,0.6)',
                  }}
                  data-testid="burn-portal-live-progress-bar"
                />
              </div>
            );
          })()}
          {chain.season?.paused && (
            <p className="text-[10px] mt-2" style={{ color: '#ff9500', fontFamily: "'Courier New', monospace" }}>
              ⚠ Season paused on chain — burns disabled
            </p>
          )}
          <style>{`
            @keyframes gfg-live-pulse { 0%,100% { opacity: .55; transform: scale(.92);} 50% { opacity: 1; transform: scale(1.08);} }
          `}</style>
        </div>
      )}
      {/* ═══ TOKEN NOT LAUNCHED MODAL ═══ */}
      <AnimatePresence>
        {showLaunchModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(12px)' }}
            onClick={() => setShowLaunchModal(false)}
            data-testid="token-launch-modal-backdrop"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative max-w-lg w-full p-6 md:p-8 overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #0a0500 0%, #0d0300 100%)',
                border: '1px solid rgba(255,149,0,0.4)',
                boxShadow: '0 0 50px rgba(255,107,0,0.15), inset 0 0 20px rgba(255,107,0,0.08)',
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="token-launch-modal"
            >
              {/* Danger stripe top */}
              <div
                className="absolute top-0 left-0 right-0 h-2"
                style={{ background: 'repeating-linear-gradient(45deg, #ff9500, #ff9500 10px, #1a0800 10px, #1a0800 20px)' }}
              />
              {/* Close X */}
              <button
                onClick={() => setShowLaunchModal(false)}
                data-testid="token-launch-modal-close"
                className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-orange-500/10 transition-colors"
                style={{ color: 'rgba(255,149,0,0.5)' }}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Rocket icon with glow */}
              <motion.div
                className="relative w-16 h-16 mx-auto mb-4 mt-3"
                animate={{ y: [0, -3, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <motion.div
                  className="absolute pointer-events-none"
                  style={{
                    inset: '-24px',
                    background: 'radial-gradient(circle, rgba(255,149,0,0.5) 0%, rgba(255,107,0,0.2) 35%, transparent 70%)',
                    filter: 'blur(16px)',
                  }}
                  animate={{ opacity: [0.6, 1, 0.6], scale: [0.95, 1.08, 0.95] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div
                  className="relative w-full h-full rounded-full flex items-center justify-center"
                  style={{
                    background: 'radial-gradient(circle at 50% 60%, #ff9500 0%, #7c3a00 65%, #1a0500 100%)',
                    boxShadow: 'inset 0 0 12px rgba(0,0,0,0.5), 0 0 24px rgba(255,149,0,0.35)',
                  }}
                >
                  <Rocket className="w-8 h-8" style={{ color: '#fff3c4', filter: 'drop-shadow(0 0 8px #ffd700)' }} />
                </div>
              </motion.div>

              <h3
                className="text-2xl md:text-3xl text-center mb-3 uppercase"
                style={{
                  fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                  color: '#ff9500',
                  textShadow: '0 0 18px rgba(255,149,0,0.5), 0 0 36px rgba(255,107,0,0.2)',
                  letterSpacing: '0.08em', fontWeight: 700,
                }}
              >
                Awaiting Launch
              </h3>
              <p
                className="text-center mb-2 leading-relaxed"
                style={{
                  fontFamily: "'Courier New', monospace",
                  color: 'rgba(255,180,130,0.75)',
                  fontSize: '13px', fontWeight: 600,
                }}
              >
                The $GFG token has not yet launched on Solana.
              </p>
              <p
                className="text-center mb-6 leading-relaxed px-2"
                style={{
                  fontFamily: "'Courier New', monospace",
                  color: 'rgba(255,180,130,0.55)',
                  fontSize: '12px',
                }}
              >
                The Burn Portal will activate the moment $GFG goes live. Until then, keep earning Season Points through the arcade — your progress carries into Season 1.
              </p>

              {/* Status strip */}
              <div className="flex items-center justify-center gap-2 mb-6 py-2 px-3" style={{ background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.2)' }}>
                <motion.span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#ff9500', boxShadow: '0 0 6px #ff9500' }}
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                />
                <span
                  className="text-[10px] tracking-[0.3em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: '#ffb570' }}
                >
                  Token launch · Pending
                </span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowLaunchModal(false)}
                  data-testid="token-launch-modal-dismiss"
                  className="flex-1 py-3 uppercase text-sm tracking-wider transition-all"
                  style={{
                    fontFamily: "'Cinzel', serif", fontWeight: 700,
                    background: 'rgba(255,215,0,0.06)',
                    border: '1px solid rgba(255,215,0,0.3)',
                    color: '#ffd700',
                  }}
                >
                  Understood
                </button>
                <button
                  onClick={() => { setShowLaunchModal(false); window.location.href = '/arcade'; }}
                  data-testid="token-launch-modal-arcade"
                  className="flex-1 py-3 uppercase text-sm tracking-wider transition-all"
                  style={{
                    fontFamily: "'Cinzel', serif", fontWeight: 700,
                    background: 'rgba(255,107,0,0.1)',
                    border: '1px solid rgba(255,149,0,0.5)',
                    color: '#ff9500',
                  }}
                >
                  Play Arcade
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* COMPACT HEADER — single line */}
      <div className="relative z-30 text-center pb-1 md:pb-2">
        <div className="inline-flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
          <motion.div
            animate={{ rotate: [-6, 6, -6], scale: [1, 1.12, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Flame className="w-5 h-5 md:w-7 md:h-7" style={{ color: '#ff4500', filter: 'drop-shadow(0 0 8px #ff6b00)' }} />
          </motion.div>
          <h2
            className="text-xl md:text-4xl font-bold tracking-[0.12em] md:tracking-[0.18em] uppercase leading-none"
            style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              background: 'linear-gradient(180deg, #fbbf24 0%, #ef4444 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 12px rgba(239,68,68,0.35))',
            }}
          >
            The Burn Portal
          </h2>
          <motion.div
            animate={{ rotate: [6, -6, 6], scale: [1, 1.12, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Flame className="w-5 h-5 md:w-7 md:h-7 scale-x-[-1]" style={{ color: '#ff4500', filter: 'drop-shadow(0 0 8px #ff6b00)' }} />
          </motion.div>
        </div>
        <p
          className="text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.28em] uppercase leading-snug px-2"
          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.5)' }}
        >
          <span className="hidden sm:inline">Sacrifice $GFG · Earn Season Points · Shrink supply forever</span>
          <span className="sm:hidden">Burn $GFG · Earn SP · Shrink supply</span>
        </p>
      </div>

      {/* COMPACT TIER CHIPS — single row, small */}
      <div className="relative z-10 grid grid-cols-3 gap-2 md:gap-3 mb-1.5 w-full">
        {tiers.map((t, i) => {
          const isSelected = selected === t.amount && !hasCustom;
          const tierColor = i === 0 ? '#fbbf24' : i === 1 ? '#ff8c00' : '#ef4444';
          return (
            <motion.button
              key={t.amount}
              onClick={() => { setSelected(t.amount); setCustomAmount(''); setResult(null); }}
              data-testid={`burn-tier-${t.amount}`}
              className="relative overflow-hidden py-3 md:py-5 px-2 md:px-3 transition-all group flex flex-col items-center"
              style={{
                background: isSelected
                  ? `linear-gradient(180deg, ${tierColor}28 0%, rgba(15,5,3,0.9) 100%)`
                  : 'rgba(20,8,4,0.5)',
                border: `1px solid ${isSelected ? `${tierColor}90` : 'rgba(239,68,68,0.2)'}`,
                boxShadow: isSelected ? `0 0 18px ${tierColor}35, inset 0 0 12px ${tierColor}18` : 'none',
              }}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="flex items-center gap-1 md:gap-1.5 mb-1">
                <Flame className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" style={{ color: isSelected ? tierColor : 'rgba(255,150,100,0.35)' }} />
                <span
                  className="text-[9px] md:text-xs tracking-[0.1em] md:tracking-[0.15em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: isSelected ? tierColor : 'rgba(255,150,100,0.45)' }}
                >
                  {t.tier}
                </span>
                {t.bonus && (
                  <span
                    className="text-[7px] md:text-[9px] font-bold px-1 md:px-1.5 leading-tight"
                    style={{
                      fontFamily: "'Courier New', monospace",
                      background: isSelected ? `${tierColor}40` : 'rgba(239,68,68,0.12)',
                      color: isSelected ? '#fff' : 'rgba(255,200,150,0.55)',
                      border: `1px solid ${isSelected ? `${tierColor}80` : 'rgba(239,68,68,0.25)'}`,
                    }}
                  >
                    {t.bonus}
                  </span>
                )}
              </div>
              <p
                className="text-base md:text-2xl font-bold leading-tight"
                style={{
                  fontFamily: "'Impact', sans-serif",
                  color: isSelected ? '#fff' : 'rgba(255,200,160,0.65)',
                  textShadow: isSelected ? `0 0 8px ${tierColor}90` : 'none',
                  letterSpacing: '0.02em',
                }}
              >
                {t.label}
                <span className="text-[9px] md:text-xs opacity-60 ml-0.5 md:ml-1">$GFG</span>
              </p>
              <p
                className="text-xs md:text-sm font-bold leading-tight mt-0.5"
                style={{
                  fontFamily: "'Impact', sans-serif",
                  color: isSelected ? '#fbbf24' : 'rgba(251,191,36,0.55)',
                  filter: isSelected ? 'drop-shadow(0 0 4px rgba(251,191,36,0.6))' : 'none',
                }}
              >
                +{t.sp.toLocaleString()} SP
              </p>
            </motion.button>
          );
        })}
      </div>

      {/* TIER MULTIPLIER METER — gradient scale showing where the current amount sits */}
      <div className="relative z-10 w-full mb-1.5">
        <div
          className="relative h-7 md:h-9 overflow-hidden"
          style={{
            background: 'linear-gradient(90deg, rgba(251,191,36,0.12) 0%, rgba(255,140,0,0.2) 50%, rgba(239,68,68,0.3) 100%)',
            border: '1px solid rgba(239,68,68,0.25)',
          }}
          data-testid="burn-tier-meter"
        >
          <div className="absolute inset-0 flex items-center justify-between px-2 md:px-3 text-[9px] md:text-[11px] tracking-[0.1em] md:tracking-[0.18em] uppercase font-bold pointer-events-none normal-case" style={{ fontFamily: "'Courier New', monospace", textTransform: 'none' }}>
            <span style={{ color: 'rgba(251,191,36,0.8)' }}>Ember · 1.0x</span>
            <span style={{ color: 'rgba(255,140,0,0.85)' }}>Pyre · 1.15x</span>
            <span style={{ color: 'rgba(239,68,68,0.9)' }}>Inferno · 1.25x</span>
          </div>
          {burnAmount > 0 && (() => {
            const pct = burnAmount >= 100000 ? 92 : burnAmount >= 50000 ? 58 : burnAmount >= 10000 ? 25 : Math.max(4, (burnAmount / 10000) * 25);
            const c = burnAmount >= 100000 ? '#ef4444' : burnAmount >= 50000 ? '#ff8c00' : '#fbbf24';
            return (
              <motion.div
                key={burnAmount}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 20 }}
                className="absolute top-0 bottom-0 w-[3px] pointer-events-none"
                style={{
                  left: `${pct}%`,
                  background: 'linear-gradient(180deg, #ffffff, #ffd700, #ff6b00)',
                  boxShadow: `0 0 10px 2px ${c}, 0 0 20px 4px ${c}80`,
                }}
              />
            );
          })()}
        </div>
      </div>

      {/* COMPACT CUSTOM AMOUNT INPUT */}
      <div className="relative z-10 w-full mb-1.5">
        <div className="relative">
          <input
            type="number"
            inputMode="numeric"
            value={customAmount}
            onChange={(e) => { setCustomAmount(e.target.value); if (e.target.value) setSelected(null); setResult(null); }}
            placeholder="Custom amount (min 1,000)"
            data-testid="burn-amount-input"
            className="w-full px-4 py-3 md:py-4 text-sm md:text-base outline-none transition-all"
            style={{
              fontFamily: "'Courier New', monospace",
              background: hasCustom ? 'linear-gradient(180deg, rgba(239,68,68,0.12) 0%, rgba(15,5,3,0.9) 80%)' : 'rgba(15,5,3,0.6)',
              border: `1px solid ${hasCustom ? 'rgba(239,68,68,0.7)' : 'rgba(239,68,68,0.2)'}`,
              color: '#fbbf24',
              boxShadow: hasCustom ? '0 0 14px rgba(239,68,68,0.18), inset 0 0 10px rgba(239,68,68,0.06)' : 'none',
              letterSpacing: '0.05em',
            }}
          />
          {hasCustom && previewTier && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 md:gap-1.5 pointer-events-none">
              <span
                className="hidden sm:inline text-[9px] tracking-[0.15em] uppercase font-bold"
                style={{ fontFamily: "'Courier New', monospace", color: previewTier.color }}
              >
                {previewTier.name}{previewTier.bonus ? ` · ${previewTier.bonus}` : ''}
              </span>
              <motion.span
                key={previewSP}
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                className="text-xs md:text-sm font-black leading-none"
                style={{
                  fontFamily: "'Impact', sans-serif",
                  background: 'linear-gradient(180deg, #ffd700, #ff8c00)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 6px rgba(251,191,36,0.5))',
                }}
                data-testid="burn-sp-preview-value"
              >
                +{previewSP.toLocaleString()} SP
              </motion.span>
            </div>
          )}
        </div>
        {customAmount && !hasCustom && parsedCustom > 0 && (
          <p className="mt-0.5 text-[8px] md:text-[9px] tracking-wider" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,140,100,0.6)' }}>
            Minimum burn is 1,000 $GFG
          </p>
        )}
      </div>

      {/* RESULT — Compact cinematic card */}
      <AnimatePresence>
        {result && !result.error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            className="relative z-10 p-3 mb-2 text-center overflow-hidden flex flex-col items-center justify-center max-w-3xl mx-auto w-full"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(239,68,68,0.2), rgba(10,2,1,0.9) 70%)',
              border: '1px solid rgba(251,191,36,0.5)',
              boxShadow: '0 0 25px rgba(239,68,68,0.2), inset 0 0 18px rgba(239,68,68,0.08)',
            }}
          >
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full"
                style={{ left: `${15 + i * 13}%`, bottom: '8px', background: '#fbbf24', boxShadow: '0 0 6px #fbbf24' }}
                initial={{ y: 0, opacity: 0 }}
                animate={{ y: -60, opacity: [0, 1, 0] }}
                transition={{ duration: 2, delay: i * 0.1 }}
              />
            ))}
            <div className="relative flex flex-col items-center justify-center w-full">
              <div className="inline-flex items-center gap-1.5 mb-1">
                <Flame className="w-3 h-3" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 4px #ef4444)' }} />
                <span
                  className="text-[9px] md:text-[10px] tracking-[0.25em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: '#ef4444' }}
                >
                  Sacrifice Complete
                </span>
                <Flame className="w-3 h-3" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 4px #ef4444)' }} />
              </div>
              <p
                className="text-[11px] md:text-xs font-bold mb-1 text-center"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.85)' }}
              >
                {result.amount_burned?.toLocaleString()} $GFG burned forever
              </p>
              <p
                className="text-2xl md:text-3xl font-bold leading-none text-center"
                style={{
                  fontFamily: "'Impact', sans-serif",
                  background: 'linear-gradient(180deg, #ffd700, #ff8c00)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 10px rgba(251,191,36,0.6))',
                }}
              >
                +{result.final_points?.toLocaleString()} SP
              </p>
              {result.tier && (
                <p
                  className="text-[9px] md:text-[10px] tracking-[0.2em] uppercase mt-1"
                  style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.6)' }}
                >
                  {result.tier} tier · {result.multiplier}x multiplier
                </p>
              )}
              {result.skill_floor_applied && result.final_points === 0 && (
                <div
                  className="mt-1.5 px-2 py-1 text-[9px] md:text-[10px] leading-snug"
                  style={{ fontFamily: "'Courier New', monospace", background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', color: 'rgba(220,200,255,0.8)' }}
                >
                  <span className="font-bold tracking-wider" style={{ color: '#c084fc' }}>SKILL FLOOR</span> · Play arcade to unlock burn SP (cap: 3× arcade)
                </div>
              )}
              {/* Live-mode: real on-chain Explorer link */}
              {result.live && result.signature && (
                <a
                  href={result.explorer || explorerTx(result.signature)}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="burn-live-explorer"
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-[0.22em] hover:brightness-125 transition"
                  style={{
                    fontFamily: "'Courier New', monospace",
                    color: '#7dd0ff',
                    background: 'rgba(8,18,28,0.85)',
                    border: '1px solid rgba(125,208,255,0.45)',
                    boxShadow: '0 0 10px rgba(125,208,255,0.18)',
                  }}
                >
                  <span className="inline-block rounded-full" style={{ width: 5, height: 5, background: '#7dd0ff', boxShadow: '0 0 6px #7dd0ff' }} />
                  ON-CHAIN · view tx
                  <ExternalLink className="w-3 h-3 opacity-80" />
                </a>
              )}
            </div>
          </motion.div>
        )}
        {result?.error && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="relative z-10 p-2 mb-2 text-center max-w-3xl mx-auto w-full"
            style={{ background: 'rgba(255,64,64,0.1)', border: '1px solid rgba(255,64,64,0.4)' }}
          >
            <p className="text-xs font-bold tracking-wider" style={{ color: '#ff4040', fontFamily: "'Courier New', monospace" }}>{result.error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* COMPACT BURN BUTTON */}
      <div className="relative z-10 w-full">
        <motion.button
          onClick={handleBurn}
          disabled={!burnAmount || burnAmount < minBurn || burning}
          data-testid="burn-action-button"
          className="relative w-full py-4 md:py-5 font-bold tracking-[0.12em] md:tracking-[0.2em] uppercase transition-all overflow-hidden group"
          style={{
            fontFamily: "'Cinzel', serif",
            background: burnAmount
              ? 'linear-gradient(180deg, rgba(239,68,68,0.25), rgba(124,30,0,0.55))'
              : 'rgba(239,68,68,0.04)',
            border: `1px solid ${burnAmount ? 'rgba(239,68,68,0.65)' : 'rgba(239,68,68,0.12)'}`,
            color: burnAmount ? '#ffd700' : 'rgba(239,68,68,0.25)',
            boxShadow: burnAmount ? '0 0 22px rgba(239,68,68,0.25), inset 0 0 18px rgba(239,68,68,0.12)' : 'none',
            fontSize: '1rem',
            textShadow: burnAmount ? '0 0 8px rgba(255,215,0,0.5)' : 'none',
          }}
          whileHover={burnAmount ? { scale: 1.005 } : {}}
          whileTap={burnAmount ? { scale: 0.98 } : {}}
        >
          {burnAmount && !burning && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.2), transparent)', width: '30%' }}
              animate={{ x: ['-100%', '400%'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
            />
          )}
          {burning ? (
            <motion.span
              className="inline-flex items-center gap-2 relative text-xs"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}>
                <Flame className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </motion.span>
              {burnStep === 'signing' ? 'Sign in wallet...' : burnStep === 'confirming' ? 'Confirming on-chain...' : 'Verifying burn...'}
            </motion.span>
          ) : (
            <span className="inline-flex items-center gap-2 md:gap-3 relative text-xs md:text-sm">
              <Flame className="w-3 h-3 md:w-4 md:h-4" />
              <span className="truncate max-w-[85%]">
                {burnAmount
                  ? `Burn ${burnAmount.toLocaleString()} $GFG`
                  : 'Select an offering'}
              </span>
              <Flame className="w-3 h-3 md:w-4 md:h-4 scale-x-[-1]" />
            </span>
          )}
        </motion.button>

        <p
          className="text-center mt-1.5 text-[8px] md:text-[9px] tracking-[0.2em] uppercase leading-relaxed"
          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.4)' }}
        >
          {isDemo
            ? 'Demo · Simulated burn · Metrics persist · No tokens destroyed'
            : !walletAddress
              ? 'Connect Solana wallet to burn $GFG'
              : 'Tokens destroyed forever · Logarithmic SP curve · Skill floor 3×'}
        </p>
      </div>
    </div>
  );
}

/* ═══ PANEL — Shared wrapper ═══ */
const Panel = ({ children, className = '', glow = false, style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} {...props}
    style={{
      background: glow ? 'rgba(20,6,4,0.85)' : 'rgba(15,5,3,0.8)',
      border: `1px solid rgba(239,68,68,${glow ? '0.5' : '0.25'})`,
      backdropFilter: 'blur(10px)',
      boxShadow: glow
        ? '0 0 30px rgba(239,68,68,0.15), 0 0 60px rgba(255,69,0,0.05), inset 0 0 25px rgba(239,68,68,0.06)'
        : '0 0 15px rgba(239,68,68,0.04), inset 0 0 15px rgba(255,69,0,0.03)',
      ...style,
    }}>
    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l z-10" style={{ borderColor: 'rgba(255,100,50,0.5)' }} />
    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r z-10" style={{ borderColor: 'rgba(255,100,50,0.5)' }} />
    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l z-10" style={{ borderColor: 'rgba(255,100,50,0.5)' }} />
    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r z-10" style={{ borderColor: 'rgba(255,100,50,0.5)' }} />
    <motion.div className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(239,68,68,0.06) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    {children}
  </div>
);


export default function SeasonsPage() {
  const navigate = useNavigate();
  const { publicKey, connected } = useWallet();
  const { isDemo, demoWallet } = useDemo();
  const { isLive } = useAppMode();
  // In demo mode, use the persistent demo wallet so scores/missions persist on the real backend
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : '');
  const [season, setSeason] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbMeta, setLbMeta] = useState(null);
  const [myPoints, setMyPoints] = useState(null);
  const [missions, setMissions] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && ['overview', 'info', 'missions', 'burn', 'leaderboard'].includes(t)) {
      setTab(t);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchData = useCallback(async () => {
    try {
      // ─── LIVE MODE: read everything from on-chain devnet program ───
      if (isLive) {
        try {
          const onSeason = await readSeason();
          if (!onSeason) {
            setSeason(null);
          } else {
            const milestone = fromRaw(onSeason.burn_milestone);
            const burned = fromRaw(onSeason.total_burned);
            const pct = milestone > 0 ? (burned / milestone) * 100 : 0;
            const rewardPool = fromRaw(onSeason.reward_pool);
            const nextRewardPool = fromRaw(onSeason.next_reward_pool);
            const end_iso = onSeason.end_time ? new Date(onSeason.end_time * 1000).toISOString() : null;
            setSeason({
              id: `onchain-s${onSeason.season_number}`,
              season_number: onSeason.season_number,
              name: `Season ${onSeason.season_number} · Devnet`,
              status: onSeason.settled ? 'settled' : (onSeason.paused ? 'paused' : 'active'),
              burn_portal_enabled: !onSeason.paused && !onSeason.settled,
              token_launched: true,
              total_burned: burned,
              burn_target: milestone,
              current_milestone_pct: pct,
              reward_pool: rewardPool,
              next_reward_pool: nextRewardPool,
              // UI consumers read `prize_pool` — alias the active pool.
              prize_pool: rewardPool,
              participants: 0,
              end_date: end_iso,
              is_demo: false,
              is_live: true,
            });
          }
        } catch (e) {
          setSeason(null);
        }
        // On-chain leaderboard / missions are not yet wired (would require
        // scanning every PlayerAccount PDA). Show empty for now — the live
        // banner explains why.
        setLeaderboard([]);
        setLbMeta(null);
        setMissions([]);
        if (walletAddress) {
          try {
            const pRes = await fetch(`${API_URL}/api/seasons/points/${walletAddress}`);
            const pData = await pRes.json();
            // We still let the user see their (off-chain) accrued SP for context.
            if (pData.points !== undefined) setMyPoints(pData);
          } catch {}
        }
        setLoading(false);
        return;
      }

      const [seasonRes, lbRes] = await Promise.all([
        fetch(`${API_URL}/api/seasons/current${isDemo ? '?include_demo=true' : ''}`), fetch(`${API_URL}/api/seasons/leaderboard`),
      ]);
      const [seasonData, lbData] = await Promise.all([seasonRes.json(), lbRes.json()]);
      // Hide demo seasons from non-demo visitors
      if (seasonData.season?.is_demo && !isDemo) {
        setSeason(null);
      } else {
        setSeason(seasonData.season);
      }
      setLeaderboard(lbData.leaderboard || []);
      if (lbData.meta) setLbMeta(lbData.meta);
      if (walletAddress) {
        try {
          const mRes = await fetch(`${API_URL}/api/seasons/missions/${walletAddress}`);
          const mData = await mRes.json();
          if (mData.missions) setMissions(mData.missions);
        } catch {}
      }
    } catch {}
    setLoading(false);
  }, [walletAddress, isDemo, isLive]);

  const fetchMyPoints = useCallback(async () => {
    if (!walletAddress) return;
    try { const res = await fetch(`${API_URL}/api/seasons/points/${walletAddress}`); const data = await res.json(); setMyPoints(data.points); } catch {}
  }, [walletAddress]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMyPoints(); }, [fetchMyPoints]);
  useEffect(() => { const i = setInterval(() => { fetchData(); if (walletAddress) fetchMyPoints(); }, 30000); return () => clearInterval(i); }, [fetchData, fetchMyPoints, walletAddress]);

  const phase = season?.phase;
  const pConfig = phase ? (PHASE_CONFIG[phase.phase] || PHASE_CONFIG.grind) : null;
  const escalation = season?.escalation;
  const escTheme = escalation ? ESCALATION_THEMES[escalation.name] || ESCALATION_THEMES.calm : null;
  // Live mode writes `current_milestone_pct` from on-chain;
  // demo/normal mode writes `burn_progress` from Mongo. Read both.
  const burnPct = Math.round(
    season?.burn_progress ?? season?.current_milestone_pct ?? 0
  );

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center" style={{ background: '#0a0200' }}>
        <motion.div animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 2, repeat: Infinity }}>
          <Flame className="w-10 h-10" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 20px rgba(239,68,68,0.6))' }} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen md:h-screen md:overflow-hidden overflow-y-auto relative" data-testid="seasons-page"
      style={{ background: '#080200' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes lavaPulse { 0%,100% { box-shadow: 0 0 25px rgba(239,68,68,0.4), 0 0 60px rgba(255,69,0,0.15); } 50% { box-shadow: 0 0 50px rgba(239,68,68,0.6), 0 0 100px rgba(255,69,0,0.3); } }
        @keyframes heatDistort { 0%,100% { transform: scaleY(1); } 50% { transform: scaleY(1.015); } }
        @keyframes ambientPulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes heroGlow { 0%,100% { opacity: 0.08; } 50% { opacity: 0.16; } }
      `}</style>

      {/* ═══ Background: uniform warm tone ═══ */}
      <div className="absolute inset-0 z-0"
        style={{ background: '#150505' }} />
      <div className="absolute inset-0 z-0"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(40,12,8,0.6) 0%, transparent 70%)' }} />

      {/* ═══ Ambient glow spots — multiple bright spots ═══ */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        {/* Center warm glow */}
        <motion.div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(239,68,68,0.12) 0%, transparent 50%)',
        }} animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 4, repeat: Infinity }} />
        {/* Left ember glow */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 15% 75%, rgba(255,69,0,0.1) 0%, transparent 35%)',
        }} />
        {/* Right gold glow */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 85% 75%, rgba(251,191,36,0.06) 0%, transparent 30%)',
        }} />
        {/* Top warm wash */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 10%, rgba(255,100,30,0.06) 0%, transparent 40%)',
        }} />
      </div>

      {/* ═══ CRT Scanline ═══ */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.25) 50%), linear-gradient(90deg, rgba(255,0,0,0.04), rgba(0,255,0,0.015), rgba(0,0,255,0.04))',
        backgroundSize: '100% 4px, 3px 100%', opacity: 0.15,
      }} />
      {/* ═══ Vignette — very subtle, edges only ═══ */}
      <div className="fixed inset-0 z-[55] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(5,1,0,0.3) 100%)' }} />

      {/* ═══ Particles ═══ */}
      <div className="absolute inset-0 pointer-events-none z-[2] overflow-hidden">
        {[...Array(22)].map((_, i) => (
          <motion.div key={`ember-${i}`} className="absolute rounded-full"
            style={{
              left: `${1 + i * 4.5}%`, bottom: '-10px',
              width: `${2 + Math.random() * 4}px`, height: `${2 + Math.random() * 4}px`,
              background: i % 3 === 0 ? '#ff6b2b' : i % 3 === 1 ? '#ef4444' : '#fbbf24',
              boxShadow: `0 0 ${8 + Math.random() * 8}px ${i % 2 === 0 ? 'rgba(255,107,43,0.9)' : 'rgba(239,68,68,0.8)'}`,
            }}
            animate={{ y: [0, -750], x: [0, (Math.random() - 0.5) * 50], opacity: [0, 0.9, 0.4, 0], scale: [0.5, 1.2, 0.3] }}
            transition={{ duration: 4 + Math.random() * 3, delay: i * 0.25, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
        {[...Array(3)].map((_, i) => (
          <motion.div key={`fog-${i}`} className="absolute"
            style={{
              width: `${600 + i * 100}px`, height: `${180 + i * 40}px`,
              top: `${20 + i * 25}%`, left: '-35%',
              background: `radial-gradient(ellipse, rgba(${i % 2 === 0 ? '239,68,68' : '255,107,43'},0.04) 0%, transparent 70%)`,
              filter: 'blur(50px)',
            }}
            animate={{ x: ['0vw', '160vw'] }}
            transition={{ duration: 30 + i * 8, delay: i * 5, repeat: Infinity, ease: 'linear' }}
          />
        ))}
      </div>

      {/* ═══ Back ═══ */}
      <BackButton to="/" />

      {/* ═══ LAYOUT ═══ */}
      <div className="relative z-10 md:h-full flex flex-col">

        {/* ═══ HEADER ═══ */}
        <div className="flex-shrink-0 px-4 md:px-6 lg:px-10 pt-20 md:pt-4 pb-1">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-2 md:gap-0">
            <div className="pl-0 md:pl-28 relative">
              {/* Warm glow bed behind title */}
              <div className="absolute -inset-x-8 -inset-y-4 pointer-events-none" style={{
                background: 'radial-gradient(ellipse at 40% 60%, rgba(255,140,0,0.12) 0%, rgba(239,68,68,0.06) 40%, transparent 70%)',
                filter: 'blur(15px)',
              }} />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1 relative"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,180,130,0.7)' }}>
                {season?.theme || 'Burn. Compete. Earn.'}
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
                className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
                style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="season-title">
                <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(255,140,0,0.3)', filter: 'blur(8px)' }}>
                  {season?.name?.toUpperCase() || 'AWAKENING'}
                </span>
                <span className="relative" style={{
                  background: 'linear-gradient(180deg, #ffd700 0%, #ffcc00 15%, #ffaa00 30%, #ff8c00 50%, #ff6b2b 70%, #ef4444 90%, #dc2626 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 30px rgba(255,170,0,0.7)) drop-shadow(0 0 60px rgba(255,100,0,0.35)) drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                }}>{season?.name?.toUpperCase() || 'AWAKENING'}</span>
                <motion.span className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.5) 50%, transparent 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
                  animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>
                  {season?.name?.toUpperCase() || 'AWAKENING'}
                </motion.span>
                <motion.span className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, #ffd700 0%, #ff8c00 50%, #ff4500 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                  animate={{ opacity: [0, 0.4, 0, 0, 0.3, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>
                  {season?.name?.toUpperCase() || 'AWAKENING'}
                </motion.span>
              </motion.h1>
            </div>

            {season && (
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                {pConfig && (
                  <div className="flex items-center gap-2 px-4 py-2"
                    style={{ border: `1px solid ${pConfig.color}60`, background: `${pConfig.color}15`, boxShadow: `0 0 15px ${pConfig.color}20` }}>
                    <motion.div className="w-2 h-2 rounded-full" style={{ background: pConfig.color, boxShadow: `0 0 8px ${pConfig.color}` }}
                      animate={{ opacity: [0.5, 1, 0.5], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 1.5, repeat: Infinity }} />
                    <span className="text-xs font-bold tracking-[0.15em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: pConfig.color }}>{pConfig.label}</span>
                    {phase.burn_multiplier > 1 && (
                      <span className="text-xs font-bold px-1.5 py-0.5"
                        style={{ fontFamily: "'Impact', sans-serif", color: '#000', background: pConfig.color, transform: 'skewX(-12deg)' }}>{phase.burn_multiplier}x</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2 px-4 py-2"
                  style={{ border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.08)', boxShadow: '0 0 15px rgba(239,68,68,0.1)' }}>
                  <Clock className="w-4 h-4" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 4px rgba(239,68,68,0.5))' }} />
                  <span className="text-lg font-bold tracking-wider" style={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    background: 'linear-gradient(180deg, #fbbf24, #ef4444)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}><TimeRemaining endDate={season.end_date} /></span>
                </div>
              </div>
            )}
          </div>

          {/* Molten line */}
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-2 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #ef4444, #ffd700, #ef4444, transparent)' }} />

          {/* Tabs */}
          <div className="flex items-center gap-1.5 md:gap-2 mt-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {[
              { id: 'overview', label: 'War Room', icon: Swords },
              { id: 'info', label: 'Intel', icon: Info },
              { id: 'missions', label: 'Missions', icon: Award },
              { id: 'burn', label: 'Burn', icon: Flame },
              { id: 'leaderboard', label: 'Board', icon: Trophy },
            ].map(t => (
              <motion.button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 transition-all relative overflow-hidden flex-shrink-0"
                style={{
                  background: tab === t.id ? 'rgba(239,68,68,0.15)' : 'rgba(0,0,0,0.4)',
                  border: `1px solid ${tab === t.id ? 'rgba(239,68,68,0.5)' : 'rgba(239,68,68,0.1)'}`,
                  boxShadow: tab === t.id ? '0 0 15px rgba(239,68,68,0.1)' : 'none',
                }}
                whileHover={{ borderColor: 'rgba(239,68,68,0.4)', boxShadow: '0 0 12px rgba(239,68,68,0.08)' }}>
                {tab === t.id && <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #ef4444, transparent)' }} />}
                <t.icon className="w-4 h-4" style={{ color: tab === t.id ? '#ef4444' : 'rgba(255,150,100,0.35)', filter: tab === t.id ? 'drop-shadow(0 0 4px rgba(239,68,68,0.5))' : 'none' }} />
                <span className="text-[10px] md:text-xs font-bold tracking-[0.12em] uppercase whitespace-nowrap" style={{ fontFamily: "'Cinzel', serif", color: tab === t.id ? '#ef4444' : 'rgba(255,150,100,0.4)' }}>{t.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* ═══ MAIN CONTENT ═══ */}
        <div className="flex-1 min-h-0 md:overflow-hidden px-4 md:px-6 lg:px-10 pt-3 pb-4 md:pb-1">
          <AnimatePresence mode="wait">

            {/* ═══════════════════════════════════════════
                WAR ROOM — Giant progress bar + 3 panels below
               ═══════════════════════════════════════════ */}
            {tab === 'overview' && (
              <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full flex flex-col gap-3" data-testid="war-room">

                {/* ═══ HERO: THE CRUCIBLE — Giant burn progress ═══ */}
                <div className="flex-1 relative overflow-hidden" style={{
                  background: 'linear-gradient(180deg, rgba(30,8,2,0.95) 0%, rgba(35,10,3,0.9) 30%, rgba(40,12,4,0.88) 60%, rgba(50,15,5,0.92) 100%)',
                  border: '2px solid rgba(255,100,50,0.4)',
                  boxShadow: '0 0 60px rgba(239,68,68,0.15), 0 0 120px rgba(255,69,0,0.06), inset 0 0 100px rgba(255,50,0,0.06)',
                }}>
                  {/* Background image slot — replace with Midjourney asset */}
                  {/* <img src="/images/seasons/crucible_bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" style={{ mixBlendMode: 'screen' }} /> */}

                  {/* Pulsing core glow */}
                  <motion.div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(239,68,68,0.14) 0%, rgba(255,69,0,0.05) 30%, transparent 60%)' }}
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />

                  {/* Bottom heat floor — visible lava glow at base */}
                  <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
                    background: 'linear-gradient(180deg, transparent 0%, rgba(239,68,68,0.06) 40%, rgba(255,69,0,0.1) 70%, rgba(255,140,0,0.08) 100%)',
                  }} />

                  {/* Radial heat glow tracking progress */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: `radial-gradient(ellipse at ${Math.min(burnPct, 95)}% 70%, rgba(239,68,68,0.22) 0%, transparent 45%)`,
                  }} />
                  {/* Vertical warm gradient */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at 50% 35%, rgba(255,100,30,0.08) 0%, transparent 45%)',
                  }} />

                  {/* Danger stripes top + bottom */}
                  <div className="absolute top-0 left-0 right-0 h-1.5"
                    style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 8px, transparent 8px, transparent 16px)', opacity: 0.6 }} />
                  <div className="absolute bottom-0 left-0 right-0 h-1.5"
                    style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 8px, transparent 8px, transparent 16px)', opacity: 0.6 }} />

                  {/* Corner brackets */}
                  <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />
                  <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />
                  <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />
                  <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />

                  {/* Content overlay */}
                  <div className="relative z-10 h-full flex flex-col justify-between px-4 md:px-8 py-4 md:py-5">

                    {/* Top row: Season info badges — simplified on mobile */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(239,68,68,0.2)' }}>
                          <Target className="w-3 md:w-3.5 h-3 md:h-3.5" style={{ color: '#ef4444' }} />
                          <span className="text-[8px] md:text-[10px] tracking-[0.15em] md:tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,100,0.7)' }}>Burn Target</span>
                        </div>
                        {pConfig && (
                          <div className="flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5" style={{ background: `${pConfig.color}10`, border: `1px solid ${pConfig.color}30` }}>
                            <span className="text-[8px] md:text-[10px] tracking-wider uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: pConfig.color }}>{pConfig.tag}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 px-2 md:px-3 py-1 md:py-1.5" style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.35)', boxShadow: '0 0 15px rgba(255,215,0,0.1)' }}>
                        <Trophy className="w-3 md:w-3.5 h-3 md:h-3.5" style={{ color: '#ffd700', filter: 'drop-shadow(0 0 4px rgba(255,215,0,0.6))' }} />
                        <span className="text-xs md:text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#ffd700', filter: 'drop-shadow(0 0 4px rgba(255,215,0,0.3))' }}>
                          {season?.reward_range?.display || `${(season?.prize_pool || 0).toLocaleString()} $GFG`}
                        </span>
                      </div>
                    </div>

                    {/* Center: Giant percentage + burned/target */}
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 80 }}
                        className="text-center">
                        <p className="text-[11px] tracking-[0.3em] uppercase mb-2 font-bold"
                          style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,180,130,0.65)' }}>Burn Progress</p>
                        <div className="relative inline-block">
                          <span className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight" style={{
                            fontFamily: "'Impact', 'Arial Black', sans-serif",
                            background: burnPct > 80 ? 'linear-gradient(180deg, #ffd700, #ff4500, #dc2626)' :
                                        burnPct > 50 ? 'linear-gradient(180deg, #fbbf24, #ef4444)' :
                                        'linear-gradient(180deg, #ff6b4a, #ef4444, #b91c1c)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            filter: `drop-shadow(0 0 50px rgba(239,68,68,${0.4 + burnPct * 0.005})) drop-shadow(0 0 100px rgba(255,69,0,${0.15 + burnPct * 0.003})) drop-shadow(0 4px 8px rgba(0,0,0,0.6))`,
                          }}>{burnPct}</span>
                          <span className="text-3xl md:text-4xl lg:text-5xl font-bold align-top ml-1" style={{
                            fontFamily: "'Impact', sans-serif",
                            background: 'linear-gradient(180deg, #ff6b4a, #b91c1c)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            filter: 'drop-shadow(0 0 20px rgba(239,68,68,0.4))',
                          }}>%</span>
                        </div>
                        <div className="flex items-center justify-center gap-2 md:gap-4 mt-2 flex-wrap">
                          <span className="text-xs md:text-sm font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.75)' }}>
                            {(season?.total_burned || 0).toLocaleString()} <span style={{ color: 'rgba(255,150,100,0.45)' }}>burned</span>
                          </span>
                          <div className="w-[1px] h-3 md:h-4" style={{ background: 'rgba(239,68,68,0.2)' }} />
                          <span className="text-xs md:text-sm font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.75)' }}>
                            {(season?.burn_target || 0).toLocaleString()} <span style={{ color: 'rgba(255,150,100,0.45)' }}>target</span>
                          </span>
                        </div>
                      </motion.div>
                    </div>

                    {/* Bottom: THE PROGRESS BAR — full-width cinematic */}
                    <div>
                      {/* Phase markers above bar — hidden on mobile */}
                      <div className="hidden md:flex justify-between mb-1 px-1">
                        {[
                          { label: 'IGNITION', pos: '0%' },
                          { label: 'CALIBRATION', pos: '20%' },
                          { label: 'THE GRIND', pos: '45%' },
                          { label: 'FEVER', pos: '70%' },
                          { label: 'THE WIRE', pos: '90%' },
                        ].map((m, i) => (
                          <span key={i} className="text-[7px] tracking-wider uppercase font-bold"
                            style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,180,130,0.4)' }}>{m.label}</span>
                        ))}
                      </div>

                      {/* The bar itself */}
                      <div className="h-6 md:h-9 relative overflow-hidden" style={{
                        background: 'linear-gradient(180deg, rgba(20,5,0,0.7) 0%, rgba(30,8,0,0.6) 100%)',
                        border: '2px solid rgba(255,100,50,0.4)',
                        boxShadow: 'inset 0 0 20px rgba(0,0,0,0.4), 0 0 25px rgba(239,68,68,0.1), inset 0 2px 10px rgba(239,68,68,0.08)',
                      }}>
                        {/* Base glow — always visible even at 0% */}
                        <div className="absolute inset-0" style={{
                          background: 'linear-gradient(90deg, rgba(139,26,26,0.3) 0%, rgba(80,15,0,0.15) 20%, transparent 40%)',
                        }} />
                        {/* Lava fill */}
                        <motion.div className="h-full relative" initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, burnPct)}%` }}
                          transition={{ duration: 2, ease: 'easeOut' }}
                          style={{
                            background: 'linear-gradient(90deg, #4a0e00 0%, #8b1a00 15%, #991b1b 30%, #dc2626 50%, #ef4444 70%, #fbbf24 90%, #ffd700 100%)',
                            animation: 'lavaPulse 3s ease-in-out infinite',
                          }}>
                          {/* Heat shimmer on fill */}
                          <div className="absolute inset-0" style={{
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent, rgba(255,200,100,0.1), transparent)',
                            backgroundSize: '300% 100%', animation: 'shimmer 3s linear infinite',
                          }} />
                          {/* Lava bubbles at edge */}
                          <motion.div className="absolute right-0 top-0 bottom-0 w-4"
                            style={{ background: 'radial-gradient(circle at right, rgba(255,215,0,0.4) 0%, transparent 70%)' }}
                            animate={{ opacity: [0.4, 0.8, 0.4] }}
                            transition={{ duration: 1.5, repeat: Infinity }} />
                        </motion.div>

                        {/* Crack overlay on empty part */}
                        <div className="absolute inset-0 pointer-events-none" style={{
                          background: 'repeating-linear-gradient(90deg, transparent 0px, transparent 60px, rgba(239,68,68,0.03) 60px, rgba(239,68,68,0.03) 61px)',
                        }} />
                      </div>

                      {/* Percentage markers below — hidden on mobile */}
                      <div className="hidden md:flex justify-between mt-1 px-1">
                        {[0, 25, 50, 75, 100].map(n => (
                          <span key={n} className="text-[8px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.45)' }}>{n}%</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ═══ BOTTOM ROW: 3 Panels ═══ */}
                <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3" style={{ minHeight: '120px' }}>

                  {/* Panel 1: Community Burn Stats */}
                  <Panel glow className="p-4 flex flex-col justify-between">
                    <div className="flex items-center gap-2 relative z-10">
                      <Flame className="w-4 h-4" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 4px rgba(239,68,68,0.6))' }} />
                      <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#ef4444' }}>Community Burn</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 relative z-10">
                      <div className="text-center">
                        <p className="text-xl font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#ef4444', filter: 'drop-shadow(0 0 8px rgba(239,68,68,0.5))' }}>
                          {(season?.total_burned || 0).toLocaleString()}
                        </p>
                        <p className="text-[8px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.55)' }}>Burned</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#fbbf24', filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.4))' }}>
                          {(season?.burn_target || 0).toLocaleString()}
                        </p>
                        <p className="text-[8px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.55)' }}>Target</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#ff8c00', filter: 'drop-shadow(0 0 8px rgba(255,140,0,0.4))' }}>
                          {season?.total_participants || 0}
                        </p>
                        <p className="text-[8px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.55)' }}>Players</p>
                      </div>
                    </div>
                  </Panel>

                  {/* Panel 2: Escalation / Phase */}
                  <Panel className="p-4 flex flex-col justify-between"
                    style={escalation && escalation.level > 0 ? { borderColor: `${escTheme.color}40` } : {}}>
                    {escalation && escalation.level > 0 ? (
                      <>
                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center gap-2">
                            <Skull className="w-4 h-4" style={{ color: escTheme.color, filter: `drop-shadow(0 0 4px ${escTheme.color}80)` }} />
                            <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: escTheme.color }}>
                              Escalation: {escalation.name}
                            </span>
                          </div>
                          <span className="text-sm font-bold px-2 py-0.5" style={{
                            fontFamily: "'Impact', sans-serif", color: '#000', background: escTheme.color,
                            transform: 'skewX(-12deg)', boxShadow: `0 0 10px ${escTheme.color}40`,
                          }}>{escalation.reward_mult}x</span>
                        </div>
                        <p className="text-[11px] italic leading-relaxed relative z-10 my-1" style={{ fontFamily: "'Courier New', monospace", color: `${escTheme.color}90` }}>
                          "{escalation.lore}"
                        </p>
                        {season?.rollover_accumulated > 0 && (
                          <p className="text-xs font-bold relative z-10" style={{ fontFamily: "'Impact', sans-serif", color: '#fbbf24' }}>
                            Rollover: {season.rollover_accumulated.toLocaleString()} $GFG
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 relative z-10">
                          <Zap className="w-4 h-4" style={{ color: pConfig?.color || '#9ca3af', filter: `drop-shadow(0 0 4px ${pConfig?.color || '#9ca3af'}80)` }} />
                          <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: pConfig?.color || '#9ca3af' }}>
                            Phase: {pConfig?.label || 'STANDBY'}
                          </span>
                        </div>
                        <div className="h-3 relative overflow-hidden z-10" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <motion.div className="h-full" initial={{ width: 0 }}
                            animate={{ width: `${Math.min(100, ((phase?.season_day || 0) / (phase?.season_duration || 30)) * 100)}%` }}
                            transition={{ duration: 1 }}
                            style={{ background: `linear-gradient(90deg, ${pConfig?.color || '#666'}60, ${pConfig?.color || '#666'})`, boxShadow: `0 0 8px ${pConfig?.color || '#666'}40` }} />
                        </div>
                        <p className="text-xs font-bold relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.65)' }}>
                          Day {phase?.season_day || 0}/{phase?.season_duration || 30} — {pConfig?.desc || ''}
                        </p>
                      </>
                    )}
                  </Panel>

                  {/* Panel 3: My Stats */}
                  <Panel className="p-4 flex flex-col justify-between" data-testid="my-season-stats">
                    {(connected || isDemo) && myPoints ? (
                      <>
                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4" style={{ color: '#ffd700', filter: 'drop-shadow(0 0 4px rgba(255,215,0,0.5))' }} />
                            <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#ffd700' }}>Your Stats</span>
                          </div>
                          {myPoints.rank && (
                            <span className="text-xl font-bold" style={{
                              fontFamily: "'Impact', sans-serif",
                              background: 'linear-gradient(180deg, #ffd700, #ff8c00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            }}>#{myPoints.rank}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-1 relative z-10">
                          {[
                            { label: 'SP', value: myPoints.total_points?.toLocaleString() || '0', color: '#fbbf24' },
                            { label: 'Games', value: myPoints.games_played || 0, color: '#ff8c00' },
                            { label: 'Burned', value: (myPoints.tokens_burned || 0).toLocaleString(), color: '#ef4444' },
                            { label: 'Tier', value: tierMultiplierLabel(myPoints.multiplier || 1), color: TIER_COLORS[myPoints.tier] || '#9ca3af' },
                          ].map((s, i) => (
                            <div key={i} className="text-center py-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
                              <p className="text-lg font-bold leading-tight" style={{ fontFamily: "'Impact', sans-serif", color: s.color }}>{s.value}</p>
                              <p className="text-[7px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.4)' }}>{s.label}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 relative z-10">
                          <Crown className="w-4 h-4" style={{ color: 'rgba(255,215,0,0.3)' }} />
                          <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.5)' }}>Your Stats</span>
                        </div>
                        <p className="text-xs relative z-10 text-center" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.55)' }}>Connect wallet to view your stats</p>
                        <div />
                      </>
                    )}
                  </Panel>
                </div>
              </motion.div>
            )}

            {/* ═══ INFO TAB ═══ */}
            {tab === 'info' && (
              <motion.div key="info" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full overflow-y-auto max-w-4xl mx-auto" style={{ scrollbarWidth: 'none' }} data-testid="info-tab">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {/* How Seasons Work */}
                  <Panel className="p-6">
                    <div className="flex items-center gap-2 mb-5 relative z-10">
                      <div className="p-1.5" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                        <Shield className="w-5 h-5" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 4px rgba(239,68,68,0.5))' }} />
                      </div>
                      <span className="text-sm tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#ef4444' }}>How Seasons Work</span>
                    </div>
                    {[
                      { title: 'Play & Burn', desc: 'Earn SP through arcade games and burning $GFG tokens. Every action in the arcade earns Season Points.', icon: Zap, color: '#ff6b2b' },
                      { title: 'Hit the Target', desc: 'The community must collectively burn the target amount of $GFG before the timer runs out.', icon: Target, color: '#ef4444' },
                      { title: 'Earn Rewards', desc: 'At season end, rewards distribute proportionally based on your SP rank. Top burners earn the most.', icon: Award, color: '#ffd700' },
                      { title: 'Escalation', desc: 'If the community fails to hit the target, the next season escalates with higher multipliers and reduced targets.', icon: Skull, color: '#dc2626' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 mb-5 last:mb-0 relative z-10">
                        <div className="p-2 flex-shrink-0" style={{ background: `${item.color}12`, border: `1px solid ${item.color}30` }}>
                          <item.icon className="w-4 h-4" style={{ color: item.color, filter: `drop-shadow(0 0 4px ${item.color}60)` }} />
                        </div>
                        <div>
                          <p className="text-sm font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,220,180,0.85)' }}>{item.title}</p>
                          <p className="text-xs leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.7)' }}>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </Panel>

                  {/* SP Earning Guide */}
                  <Panel className="p-6">
                    <div className="flex items-center gap-2 mb-5 relative z-10">
                      <div className="p-1.5" style={{ background: 'rgba(255,107,43,0.12)', border: '1px solid rgba(255,107,43,0.25)' }}>
                        <TrendingUp className="w-5 h-5" style={{ color: '#ff6b2b', filter: 'drop-shadow(0 0 4px rgba(255,107,43,0.5))' }} />
                      </div>
                      <span className="text-sm tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#ff6b2b' }}>Earning SP</span>
                    </div>
                    <div className="space-y-1.5 relative z-10">
                      {[
                        { action: 'Safe Cracker', pts: '20 SP', color: '#ff8c00' },
                        { action: 'Snatch-a-Bag', pts: '15 SP', color: '#ff8c00' },
                        { action: 'Fee Scrambler', pts: '10 SP', color: '#ff8c00' },
                        { action: 'The Descent', pts: '50-600 SP', color: '#c026d3' },
                        { action: 'TX Graveyard', pts: '25 SP', color: '#6366f1' },
                        { action: 'Burn $GFG', pts: 'Log curve', color: '#ef4444' },
                        { action: 'Daily Streak', pts: '+1%/day', color: '#fbbf24' },
                        { action: 'Missions', pts: '500-5K SP', color: '#ffd700' },
                      ].map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3"
                          style={{ background: i % 2 === 0 ? 'rgba(0,0,0,0.3)' : 'transparent', borderLeft: `2px solid ${r.color}40` }}>
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ background: r.color, boxShadow: `0 0 4px ${r.color}80` }} />
                            <span className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,220,190,0.8)' }}>{r.action}</span>
                          </div>
                          <span className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: r.color }}>{r.pts}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-3 relative z-10" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                      <p className="text-[11px] leading-relaxed font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.75)' }}>
                        <Crown className="w-3 h-3 inline mr-1" style={{ color: '#ffd700' }} />
                        Tier multipliers: Bronze 1x to Mythic 2.5x. Burns capped at 3x arcade SP.
                      </p>
                    </div>
                  </Panel>
                </div>
              </motion.div>
            )}

            {/* ═══ MISSIONS TAB ═══ */}
            {tab === 'missions' && (
              <motion.div key="missions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full overflow-y-auto space-y-2 max-w-3xl mx-auto" style={{ scrollbarWidth: 'none' }} data-testid="missions-tab">
                {(!walletAddress && !isDemo) ? (
                  <div className="h-full flex flex-col items-center justify-center">
                    <Award className="w-12 h-12 mb-3" style={{ color: 'rgba(239,68,68,0.25)' }} />
                    <p className="text-base font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,150,0.6)' }}>Connect wallet to view missions</p>
                  </div>
                ) : missions.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center">
                    <Award className="w-12 h-12 mb-3" style={{ color: 'rgba(239,68,68,0.25)' }} />
                    <p className="text-base font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,150,0.6)' }}>No active missions</p>
                  </div>
                ) : missions.map((m, idx) => {
                  const pct = m.target > 0 ? Math.min(100, (m.current / m.target) * 100) : 0;
                  const diffColors = { medium: '#fbbf24', hard: '#ef4444', extreme: '#dc2626' };
                  const dc = diffColors[m.difficulty] || '#9ca3af';
                  return (
                    <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                      data-testid={`mission-${m.id}`}>
                      <Panel className="p-4" style={{
                        borderColor: m.claimed ? 'rgba(34,197,94,0.3)' : m.completed ? 'rgba(255,215,0,0.3)' : 'rgba(239,68,68,0.15)',
                        background: m.claimed ? 'rgba(34,197,94,0.06)' : m.completed ? 'rgba(255,215,0,0.06)' : 'rgba(10,5,2,0.7)',
                      }}>
                        <div className="flex items-start justify-between gap-3 mb-3 relative z-10">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-base font-bold" style={{ fontFamily: "'Cinzel', serif", color: m.claimed ? '#22c55e' : m.completed ? '#ffd700' : 'rgba(255,220,180,0.8)' }}>{m.title}</span>
                              <span className="text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider"
                                style={{ background: dc + '20', color: dc, border: `1px solid ${dc}40`, fontFamily: "'Impact', sans-serif" }}>{m.difficulty}</span>
                            </div>
                            <p className="text-xs" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.65)' }}>{m.description}</p>
                          </div>
                          <div className="flex-shrink-0">
                            {m.claimed ? (
                              <span className="text-xs font-bold px-3 py-1.5" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>CLAIMED</span>
                            ) : m.completed ? (
                              <motion.button
                                onClick={async () => { try { const r = await fetch(`${API_URL}/api/seasons/missions/${walletAddress}/claim/${m.id}`, { method: 'POST' }); if (r.ok) setMissions(p => p.map(x => x.id === m.id ? { ...x, claimed: true } : x)); } catch {} }}
                                data-testid={`claim-${m.id}`} className="text-xs font-bold px-4 py-2"
                                style={{ fontFamily: "'Cinzel', serif", background: 'rgba(255,215,0,0.15)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.35)', boxShadow: '0 0 12px rgba(255,215,0,0.1)' }}
                                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>CLAIM {m.reward_points} SP</motion.button>
                            ) : (
                              <span className="text-sm font-bold px-2 py-1" style={{ fontFamily: "'Impact', sans-serif", color: '#fbbf24', transform: 'skewX(-12deg)', display: 'inline-block', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)' }}>{m.reward_points} SP</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 relative z-10">
                          <div className="flex-1 h-2.5 overflow-hidden" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <motion.div className="h-full" style={{
                              background: m.claimed ? 'linear-gradient(90deg, #16a34a, #22c55e)' : m.completed ? 'linear-gradient(90deg, #b45309, #fbbf24)' : `linear-gradient(90deg, ${dc}80, ${dc})`,
                            }} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
                          </div>
                          <span className="text-[11px] flex-shrink-0 font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.65)' }}>{m.current.toLocaleString()}/{m.target.toLocaleString()}</span>
                        </div>
                      </Panel>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}

            {/* ═══ BURN PORTAL TAB ═══ */}
            {tab === 'burn' && (
              <motion.div key="burn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col" data-testid="burn-room">
                <div className="flex-1 relative overflow-hidden" style={{
                  background: 'linear-gradient(180deg, rgba(35,8,2,0.95) 0%, rgba(45,12,3,0.9) 30%, rgba(55,15,4,0.88) 60%, rgba(70,20,5,0.92) 100%)',
                  border: '2px solid rgba(255,100,50,0.4)',
                  boxShadow: '0 0 60px rgba(239,68,68,0.15), 0 0 120px rgba(255,69,0,0.06), inset 0 0 100px rgba(255,50,0,0.06)',
                }}>
                  {/* Pulsing core glow */}
                  <motion.div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(239,68,68,0.18) 0%, rgba(255,69,0,0.06) 35%, transparent 65%)' }}
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />

                  {/* Bottom heat floor */}
                  <div className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none" style={{
                    background: 'linear-gradient(180deg, transparent 0%, rgba(239,68,68,0.08) 40%, rgba(255,69,0,0.14) 70%, rgba(255,140,0,0.1) 100%)',
                  }} />

                  {/* Danger stripes top + bottom */}
                  <div className="absolute top-0 left-0 right-0 h-1.5"
                    style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 8px, transparent 8px, transparent 16px)', opacity: 0.6 }} />
                  <div className="absolute bottom-0 left-0 right-0 h-1.5"
                    style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 8px, transparent 8px, transparent 16px)', opacity: 0.6 }} />

                  {/* Corner brackets */}
                  <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />
                  <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />
                  <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />
                  <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 z-10" style={{ borderColor: 'rgba(255,100,50,0.55)' }} />

                  {/* Ember particles rising */}
                  {[...Array(18)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 rounded-full pointer-events-none"
                      style={{
                        left: `${(i * 5.5 + 3) % 100}%`,
                        bottom: '-10px',
                        background: i % 2 ? '#ff6b00' : '#fbbf24',
                        boxShadow: `0 0 6px ${i % 2 ? '#ff6b00' : '#fbbf24'}`,
                        zIndex: 5,
                      }}
                      animate={{
                        y: [-10, -700],
                        opacity: [0, 1, 1, 0],
                        x: [0, (i % 2 ? 30 : -30)],
                      }}
                      transition={{
                        duration: 8 + (i % 5),
                        repeat: Infinity,
                        delay: i * 0.5,
                        ease: 'linear',
                      }}
                    />
                  ))}

                  {/* Content */}
                  <div className="relative z-10 h-full flex flex-col items-center justify-center px-2 md:px-6 py-4 md:py-8">
                    {(isDemo || isLive || (season?.burn_portal_enabled && season?.token_launched)) ? (
                      <BurnPortal walletAddress={walletAddress} isDemo={isDemo} isLive={isLive} onBurn={() => { fetchData(); fetchMyPoints(); }} />
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center">
                        <motion.div
                          animate={{ y: [0, -6, 0] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                        >
                          <Flame className="w-20 h-20 md:w-24 md:h-24 mb-6" style={{ color: 'rgba(239,68,68,0.4)', filter: 'drop-shadow(0 0 20px rgba(239,68,68,0.3))' }} />
                        </motion.div>
                        <p className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif", color: 'rgba(255,200,160,0.85)', filter: 'drop-shadow(0 0 12px rgba(239,68,68,0.2))' }}>Burn Portal Dormant</p>
                        <p className="text-sm md:text-base tracking-wider" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.55)' }}>The flame awakens when the season ignites.</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ═══ LEADERBOARD TAB ═══ */}
            {tab === 'leaderboard' && (
              <motion.div key="leaderboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full max-w-3xl mx-auto overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
                <Panel className="p-5 relative min-h-full">
                  {lbMeta && !lbMeta.blackout && lbMeta.updated_at && (
                    <div className="flex items-center justify-between mb-4 pb-2 relative z-10" style={{ borderBottom: '1px solid rgba(239,68,68,0.1)' }}>
                      <span className="text-[11px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.5)' }}>Updated: {new Date(lbMeta.updated_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                      <span className="text-[11px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,160,0.5)' }}>Refresh in {lbMeta.hours_until_refresh}h</span>
                    </div>
                  )}
                  {lbMeta?.blackout && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center" style={{ background: 'rgba(5,1,0,0.92)', backdropFilter: 'blur(6px)' }} data-testid="leaderboard-blackout">
                      <motion.div animate={{ opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 3, repeat: Infinity }} className="text-center">
                        <Eye className="w-10 h-10 mx-auto mb-4" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 15px rgba(239,68,68,0.5))' }} />
                        <p className="text-xl font-bold uppercase tracking-wider mb-2" style={{ fontFamily: "'Cinzel Decorative', serif", color: '#ef4444' }}>Positions Locked</p>
                        <p className="text-sm" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.55)' }}>Results revealed at season end</p>
                      </motion.div>
                    </div>
                  )}
                  {leaderboard.length === 0 && !lbMeta?.blackout ? (
                    <div className="text-center py-20 relative z-10" data-testid="seasons-leaderboard-empty">
                      <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: isLive ? 'rgba(125,208,255,0.2)' : 'rgba(255,180,120,0.15)' }} />
                      <p className="text-base font-bold" style={{ fontFamily: "'Cinzel', serif", color: isLive ? 'rgba(168,222,247,0.85)' : 'rgba(255,200,160,0.5)' }}>
                        {isLive ? 'No on-chain participants yet' : 'No participants yet'}
                      </p>
                      {isLive && (
                        <p className="text-[11px] mt-2 max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(168,222,247,0.55)', fontStyle: 'italic' }}>
                          Devnet is fresh — the leaderboard fills as wallets initialize their on-chain Player accounts and start burning $GFG. Be the first.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1 relative z-10">
                      {leaderboard.map((entry, i) => {
                        const rank = entry.rank || i + 1;
                        const tierColor = TIER_COLORS[entry.tier] || '#9ca3af';
                        const isTop3 = rank <= 3;
                        const rankColors = { 1: '#ffd700', 2: '#c0c0c0', 3: '#cd7f32' };
                        return (
                          <motion.div key={entry.wallet_address} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: rank * 0.03 }}
                            className="flex items-center gap-4 py-2.5 px-4"
                            style={{ background: isTop3 ? 'rgba(255,255,255,0.03)' : 'transparent', borderBottom: '1px solid rgba(239,68,68,0.06)', borderLeft: isTop3 ? `2px solid ${rankColors[rank]}40` : '2px solid transparent' }}>
                            <span className="text-lg font-bold w-8 text-center" style={{ fontFamily: "'Impact', sans-serif", color: rankColors[rank] || 'rgba(255,180,120,0.3)' }}>#{rank}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,220,180,0.75)' }}>
                                {entry.username || `${entry.wallet_address?.slice(0, 4)}...${entry.wallet_address?.slice(-4)}`}
                              </p>
                              <p className="text-[10px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: tierColor }}>{entry.tier?.toUpperCase()}</p>
                            </div>
                            <div className="text-right">
                              {entry.sp_hidden ? (
                                <p className="text-xs font-bold" style={{ color: 'rgba(255,180,120,0.2)' }}>Hidden</p>
                              ) : (
                                <p className="text-lg font-bold" style={{
                                  fontFamily: "'Impact', sans-serif", background: 'linear-gradient(180deg, #fbbf24, #ef4444)',
                                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                }}>{entry.total_points?.toLocaleString()} <span className="text-xs" style={{ color: 'rgba(255,180,130,0.45)' }}>SP</span></p>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </Panel>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ═══ BOTTOM HUD ═══ */}
        <div className="flex-shrink-0 border-t px-6 py-2 flex items-center justify-center gap-2"
          style={{ borderColor: 'rgba(239,68,68,0.18)', background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.92) 100%)', backdropFilter: 'blur(12px)' }}>
          <motion.div className="w-1.5 h-1.5 rounded-full" style={{ background: '#ef4444', boxShadow: '0 0 6px #ef4444' }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 2, repeat: Infinity }} />
          <span className="text-[9px] tracking-[0.3em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,180,130,0.55)' }}>Season Protocol Active</span>
          <div className="w-[1px] h-3 mx-2" style={{ background: 'rgba(239,68,68,0.2)' }} />
          <span className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,180,130,0.4)' }}>All games use in-game credits</span>
          <div className="w-[1px] h-3 mx-2" style={{ background: 'rgba(239,68,68,0.2)' }} />
          <span className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,180,130,0.4)' }}>Rewards based on participation</span>
        </div>
      </div>
    </div>
  );
}


// Demo Burn Portal — cinematic, dramatic, Grimoire-themed
function DemoBurnPortal() {
  const [selected, setSelected] = useState(null);
  const [burning, setBurning] = useState(false);
  const [result, setResult] = useState(null);

  const tiers = [
    { label: '10,000',  amount: 10000,  sp: 345,  bonus: null,     tier: 'Ember' },
    { label: '50,000',  amount: 50000,  sp: 580,  bonus: '+15%',   tier: 'Pyre' },
    { label: '100,000', amount: 100000, sp: 830,  bonus: '+25%',   tier: 'Inferno' },
  ];

  const handleBurn = () => {
    if (!selected) return;
    setBurning(true);
    setTimeout(() => {
      setBurning(false);
      const t = tiers.find(x => x.amount === selected);
      setResult({ amount: selected, sp: t.sp, tier: t.tier });
      setTimeout(() => setResult(null), 5000);
    }, 2200);
  };

  return (
    <div className="h-full flex flex-col">
      {/* HERO HEADER — Centered, dramatic, cinematic */}
      <div className="relative z-30 text-center pb-4 md:pb-6">
        {/* Giant pulsing flame icon */}
        <motion.div
          className="relative w-16 h-16 md:w-24 md:h-24 mx-auto mb-3 md:mb-4"
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          {/* Soft radial glow */}
          <motion.div
            className="absolute pointer-events-none"
            style={{
              inset: '-60px',
              background: 'radial-gradient(circle, rgba(239,68,68,0.5) 0%, rgba(255,107,0,0.25) 30%, transparent 65%)',
              filter: 'blur(20px)',
              zIndex: 1,
            }}
            animate={{ opacity: [0.7, 1, 0.7], scale: [0.95, 1.08, 0.95] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Flame orb */}
          <div
            className="relative w-full h-full rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle at 50% 60%, #ff4500 0%, #7c1e00 65%, #1a0500 100%)',
              boxShadow: 'inset 0 0 14px rgba(0,0,0,0.5), inset 0 -5px 10px rgba(255,140,0,0.25), 0 0 30px rgba(239,68,68,0.25)',
              zIndex: 2,
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.18, 1], rotate: [-4, 4, -4] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Flame className="w-8 h-8 md:w-12 md:h-12" style={{ color: '#fff3c4', filter: 'drop-shadow(0 0 10px #ffd700) drop-shadow(0 0 18px #ff4500)' }} />
            </motion.div>
          </div>
        </motion.div>

        {/* Title */}
        <h2
          className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-wide mb-2"
          style={{
            fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
            background: 'linear-gradient(180deg, #fbbf24 0%, #ef4444 70%, #7c1e00 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 18px rgba(239,68,68,0.4))',
          }}
        >
          The Burn Portal
        </h2>

        {/* Flourish divider */}
        <div className="flex items-center justify-center gap-2 md:gap-3 mb-2 md:mb-3">
          <div className="h-[1px] w-12 md:w-20" style={{ background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.6))' }} />
          <Flame className="w-3 h-3 md:w-4 md:h-4" style={{ color: 'rgba(239,68,68,0.7)' }} />
          <div className="h-[1px] w-12 md:w-20" style={{ background: 'linear-gradient(270deg, transparent, rgba(239,68,68,0.6))' }} />
        </div>

        {/* Subtitle */}
        <p
          className="text-[10px] md:text-sm tracking-[0.12em] md:tracking-[0.2em] uppercase px-2"
          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.6)' }}
        >
          Sacrifice $GFG. Earn Season Points. Shrink the supply forever.
        </p>
      </div>

      {/* TIER CARDS — Cinematic full-width */}
      <div className="relative z-10 grid grid-cols-3 gap-2 md:gap-5 mb-4 md:mb-6 max-w-5xl mx-auto w-full">
        {tiers.map((t, i) => {
          const isSelected = selected === t.amount;
          const tierColor = i === 0 ? '#fbbf24' : i === 1 ? '#ff8c00' : '#ef4444';
          return (
            <motion.button
              key={t.amount}
              onClick={() => { setSelected(t.amount); setResult(null); }}
              data-testid={`demo-burn-tier-${t.amount}`}
              className="relative overflow-hidden p-3 md:p-6 transition-all group"
              style={{
                background: isSelected
                  ? `linear-gradient(180deg, ${tierColor}22 0%, rgba(15,5,3,0.9) 80%)`
                  : 'rgba(20,8,4,0.5)',
                border: `1.5px solid ${isSelected ? `${tierColor}90` : 'rgba(239,68,68,0.2)'}`,
                boxShadow: isSelected
                  ? `0 0 30px ${tierColor}40, inset 0 0 25px ${tierColor}20`
                  : '0 0 15px rgba(239,68,68,0.05)',
              }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
            >
              {/* Corner brackets on each tier */}
              <div className="absolute top-1 left-1 w-3 h-3 border-t border-l" style={{ borderColor: isSelected ? tierColor : 'rgba(255,100,50,0.3)' }} />
              <div className="absolute top-1 right-1 w-3 h-3 border-t border-r" style={{ borderColor: isSelected ? tierColor : 'rgba(255,100,50,0.3)' }} />
              <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l" style={{ borderColor: isSelected ? tierColor : 'rgba(255,100,50,0.3)' }} />
              <div className="absolute bottom-1 right-1 w-3 h-3 border-b border-r" style={{ borderColor: isSelected ? tierColor : 'rgba(255,100,50,0.3)' }} />

              {/* Animated flame glow on selected */}
              {isSelected && (
                <motion.div
                  className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-32 h-16 rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(ellipse, ${tierColor}, transparent 70%)`, filter: 'blur(16px)' }}
                  animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}

              {/* Tier label at top */}
              <div className="relative flex items-center justify-center gap-1 md:gap-2 mb-2 md:mb-3">
                <Flame className="w-3 h-3 md:w-4 md:h-4" style={{ color: isSelected ? tierColor : 'rgba(255,150,100,0.35)' }} />
                <span
                  className="text-[8px] md:text-[11px] tracking-[0.2em] md:tracking-[0.3em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: isSelected ? tierColor : 'rgba(255,150,100,0.4)' }}
                >
                  {t.tier}
                </span>
              </div>

              {/* Amount — Giant */}
              <p
                className="text-xl md:text-4xl lg:text-5xl font-bold leading-none mb-1 md:mb-2"
                style={{
                  fontFamily: "'Impact', 'Arial Black', sans-serif",
                  color: isSelected ? '#fff' : 'rgba(255,200,160,0.6)',
                  textShadow: isSelected ? `0 0 16px ${tierColor}90` : 'none',
                }}
              >
                {t.label}
              </p>
              <p
                className="text-[8px] md:text-[10px] tracking-wider uppercase mb-3 md:mb-4"
                style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,180,120,0.4)' }}
              >
                $GFG
              </p>

              {/* Divider */}
              <div className="h-[1px] mb-2 md:mb-3" style={{ background: `linear-gradient(90deg, transparent, ${isSelected ? tierColor : 'rgba(239,68,68,0.2)'}, transparent)` }} />

              {/* SP reward — Big */}
              <p
                className="text-base md:text-2xl lg:text-3xl font-bold leading-none"
                style={{
                  fontFamily: "'Impact', sans-serif",
                  color: isSelected ? '#fbbf24' : 'rgba(251,191,36,0.5)',
                  filter: isSelected ? 'drop-shadow(0 0 8px rgba(251,191,36,0.6))' : 'none',
                }}
              >
                +{t.sp.toLocaleString()}
              </p>
              <p
                className="text-[8px] md:text-[10px] tracking-[0.1em] md:tracking-[0.2em] uppercase mt-0.5 md:mt-1"
                style={{ fontFamily: "'Courier New', monospace", color: 'rgba(251,191,36,0.45)' }}
              >
                <span className="hidden md:inline">Season Points</span>
                <span className="md:hidden">SP</span>
              </p>

              {/* Bonus badge */}
              {t.bonus && (
                <div
                  className="absolute top-1.5 right-1.5 md:top-2 md:right-2 px-1.5 md:px-2 py-0.5 md:py-1 text-[8px] md:text-[10px] font-bold"
                  style={{
                    fontFamily: "'Courier New', monospace",
                    background: isSelected ? `${tierColor}30` : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${isSelected ? `${tierColor}80` : 'rgba(239,68,68,0.25)'}`,
                    color: isSelected ? '#fff' : 'rgba(255,200,150,0.55)',
                  }}
                >
                  {t.bonus}
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* RESULT — Epic burn confirmation */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            className="relative z-10 p-4 sm:p-5 mb-3 text-center overflow-hidden flex flex-col items-center justify-center"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(239,68,68,0.2), rgba(10,2,1,0.9) 70%)',
              border: '1px solid rgba(251,191,36,0.5)',
              boxShadow: '0 0 35px rgba(239,68,68,0.25), inset 0 0 25px rgba(239,68,68,0.1)',
            }}
          >
            {/* Rising ember particles inside result */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  left: `${15 + i * 10}%`,
                  bottom: '10px',
                  background: '#fbbf24',
                  boxShadow: '0 0 8px #fbbf24',
                }}
                initial={{ y: 0, opacity: 0 }}
                animate={{ y: -80, opacity: [0, 1, 0] }}
                transition={{ duration: 2, delay: i * 0.1 }}
              />
            ))}

            <div className="relative flex flex-col items-center justify-center w-full">
              <motion.div
                className="inline-flex items-center gap-2 mb-2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring' }}
              >
                <Flame className="w-4 h-4" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 6px #ef4444)' }} />
                <span
                  className="text-[11px] md:text-xs tracking-[0.3em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: '#ef4444' }}
                >
                  Sacrifice Complete
                </span>
                <Flame className="w-4 h-4" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 6px #ef4444)' }} />
              </motion.div>
              <motion.p
                className="text-sm md:text-base font-bold mb-2 text-center"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,200,160,0.85)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {result.amount.toLocaleString()} $GFG burned forever
              </motion.p>
              <motion.p
                className="text-4xl sm:text-5xl font-bold leading-none text-center"
                style={{
                  fontFamily: "'Impact', sans-serif",
                  background: 'linear-gradient(180deg, #ffd700, #ff8c00)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 14px rgba(251,191,36,0.6))',
                }}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 250 }}
              >
                +{result.sp.toLocaleString()} SP
              </motion.p>
              <motion.p
                className="text-[10px] md:text-[11px] tracking-[0.25em] uppercase mt-2"
                style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.6)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
              >
                {result.tier} tier
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1" />

      {/* Burn button wrapper — constrained max-width on wide screens */}
      <div className="relative z-10 w-full max-w-3xl mx-auto">
        {/* DRAMATIC BURN BUTTON */}
        <motion.button
          onClick={handleBurn}
          disabled={!selected || burning}
          data-testid="demo-burn-btn"
          className="relative w-full py-4 md:py-5 font-bold tracking-[0.15em] md:tracking-[0.25em] uppercase transition-all overflow-hidden group"
          style={{
            fontFamily: "'Cinzel', serif",
            background: selected
              ? 'linear-gradient(180deg, rgba(239,68,68,0.22), rgba(124,30,0,0.5))'
              : 'rgba(239,68,68,0.04)',
            border: `1.5px solid ${selected ? 'rgba(239,68,68,0.6)' : 'rgba(239,68,68,0.12)'}`,
            color: selected ? '#ffd700' : 'rgba(239,68,68,0.25)',
            boxShadow: selected ? '0 0 35px rgba(239,68,68,0.25), inset 0 0 25px rgba(239,68,68,0.12)' : 'none',
            fontSize: '0.85rem',
            textShadow: selected ? '0 0 8px rgba(255,215,0,0.5)' : 'none',
          }}
          whileHover={selected ? { scale: 1.01 } : {}}
          whileTap={selected ? { scale: 0.98 } : {}}
        >
          {/* Animated ember sweep on button when selected */}
          {selected && !burning && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,140,0,0.2), transparent)',
                width: '30%',
              }}
              animate={{ x: ['-100%', '400%'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
            />
          )}
          {burning ? (
            <motion.span
              className="inline-flex items-center gap-2 relative"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}>
                <Flame className="w-4 h-4 md:w-5 md:h-5" />
              </motion.span>
              Burning...
            </motion.span>
          ) : (
            <span className="inline-flex items-center gap-2 md:gap-4 relative">
              <Flame className="w-3.5 h-3.5 md:w-5 md:h-5" />
              <span className="truncate max-w-[80%] md:text-base">{selected ? `Burn ${selected.toLocaleString()} $GFG` : 'Select an offering'}</span>
              <Flame className="w-3.5 h-3.5 md:w-5 md:h-5 scale-x-[-1]" />
            </span>
          )}
        </motion.button>

        {/* Hint */}
        <p
          className="text-center mt-3 text-[9px] md:text-[10px] tracking-[0.25em] uppercase"
          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,200,150,0.3)' }}
        >
          Demo mode · No tokens actually burned
        </p>
      </div>
    </div>
  );
}
